import React, { useEffect } from "react";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import FormSelectionField from "../../../components/formFields/FormSelectionField";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import ButtonWithLoading from "../../../components/common/ButtonWithLoading";
import { Typography } from "@progress/kendo-react-common";
import { requiredValidator } from "../../../components/formFields/CommonValidator";
import ShadowCard from "../../../components/common/ShadowCard";
import FormDatePicker from "../../../components/formFields/FormDateField";
import moment from "moment";
import { getAllActiveUsers } from "../../user/services/user.services";
import { getAllActiveSectors } from "../../sector/services/sector.services";
import { ISector } from "../../sector/sectorModel";
import {
  CONTACTTYPE,
  USERPERFORMANCEDATETYPE,
} from "../../../_contstants/common";
import { getAllActiveLeadSource } from "../../leadsource/services/leadsource.service";
import FormMultiSelectionFiled from "../../../components/formFields/FormMultiSelectionFiled";
import { getAllBranches } from "../../branch/services/branch.services";
import { IBranch } from "../../branch/branchModel";

interface ChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const ChangeWatcher: React.FC<ChangeWatcherProps> = ({ formRenderProps }) => {
  useEffect(() => {
    formRenderProps.onChange("filterFromDate", {
      value: moment().startOf("month").toDate(),
    });
    formRenderProps.onChange("filterToDate", {
      value: moment().toDate(),
    });
  }, []);
  return null;
};

const UserPerformanceReportForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.reports.loading);

  const SectorList = useAppSelector((state) => state.sector.SectorList);
  const BranchList = useAppSelector((state) => state.branch.BranchList);

  useEffect(() => {
    dispatch(getAllBranches());
    dispatch(getAllActiveUsers());
    dispatch(getAllActiveSectors());
    dispatch(getAllActiveLeadSource());
  }, []);

  const handleSubmit = (values: any) => {
    const payload: any = {};

    payload.filterFromDate = values?.filterFromDate
      ? moment(values?.filterFromDate).format("YYYY-MM-DD")
      : "0";
    payload.filterToDate = values?.filterToDate
      ? moment(values?.filterToDate).format("YYYY-MM-DD")
      : "0";
    payload.filterSector = values?.filterSector ? values?.filterSector : "0";
    payload.InquiryType = values?.InquiryType ? values?.InquiryType : "0";
    payload.BranchIDs =
      values?.BranchIDs?.length > 0 ? values?.BranchIDs?.join(",") : "0";
    payload.DateType = values?.DateType ? values?.DateType : "0";

    window.open(
      `userperformancereportview/${payload?.filterFromDate}/${payload?.filterToDate}/${payload?.filterSector}/${payload?.InquiryType}/${payload?.BranchIDs}/${payload?.DateType}`
    );
  };

  return (
    <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
      <GridLayoutItem>
        <ShadowCard>
          <Form
            onSubmit={handleSubmit}
            render={(formRenderProps: FormRenderProps) => (
              <FormElement style={{ width: "100%" }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ cols: 10, rows: 0 }}
                  cols={[{ width: "40%" }, { width: "30%" }, { width: "30%" }]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4
                      style={{ marginBottom: 0, marginLeft: "3px" }}
                    >
                      User Performance Report
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem style={{ display: "flex", gap: 10 }}>
                    <Field
                      name="filterFromDate"
                      label="From Date"
                      format="dd/MM/yyyy"
                      wrapperStyle={{ width: "100%" }}
                      component={FormDatePicker}
                      astrike={true}
                      validator={requiredValidator}
                    />
                    <Field
                      name="filterToDate"
                      label="To Date"
                      format="dd/MM/yyyy"
                      minDate={moment(
                        formRenderProps.valueGetter("filterFromDate")
                      ).toDate()}
                      wrapperStyle={{ width: "100%" }}
                      component={FormDatePicker}
                      astrike={true}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}></GridLayoutItem>
                  <GridLayoutItem style={{ display: "flex", gap: 10 }}>
                    <Field
                      name="InquiryType"
                      wrapperClassName="w-50"
                      label="Inquiry Type"
                      placeholder="Type"
                      component={FormSelectionField}
                      options={CONTACTTYPE?.map((type: any) => ({
                        value: type?.value,
                        label: type?.label,
                      }))}
                    />
                    <Field
                      name="DateType"
                      wrapperClassName="w-50"
                      label="Date Type"
                      placeholder="Type"
                      component={FormSelectionField}
                      options={USERPERFORMANCEDATETYPE?.map((type: any) => ({
                        value: type?.value,
                        label: type?.label,
                      }))}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}></GridLayoutItem>
                  <GridLayoutItem style={{ display: "flex", gap: 10 }}>
                    <Field
                      name="BranchIDs"
                      wrapperClassName="w-50"
                      label="Branches"
                      placeholder="Select Branches"
                      component={FormMultiSelectionFiled}
                      options={BranchList?.map((branch: IBranch) => ({
                        value: branch?.ID,
                        label: `${branch?.BranchName} - ${branch?.CompanyName}`,
                      }))}
                    />
                    <Field
                      name="filterSector"
                      label="Destination"
                      placeholder="Destination"
                      wrapperClassName="w-50"
                      component={FormSelectionField}
                      options={SectorList?.map((sector: ISector) => ({
                        value: sector?.ID,
                        label: sector?.SectorName,
                      }))}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}></GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      display: "flex",
                      gap: 10,
                      position: "relative",
                      minHeight: 50,
                    }}
                  >
                    <div style={{ position: "absolute", top: 20, right: 0 }}>
                      <ButtonWithLoading
                        label={"Get Details"}
                        type="submit"
                        Size="medium"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                    </div>
                  </GridLayoutItem>
                </GridLayout>
                <ChangeWatcher formRenderProps={formRenderProps} />
              </FormElement>
            )}
          />
        </ShadowCard>
      </GridLayoutItem>
    </GridLayout>
  );
};

export default UserPerformanceReportForm;
